import { useEffect } from "react";

// Canonical URLs must always assert the one true production origin, even when
// this build is served from dev.askthesea.com — otherwise Google can index the
// dev host as a duplicate of production.
const CANONICAL_ORIGIN = "https://askthesea.com";

function toCanonicalUrl(rawUrl) {
  try {
    const parsed = new URL(rawUrl);
    return CANONICAL_ORIGIN + parsed.pathname;
  } catch {
    return rawUrl;
  }
}

export default function SEO({ title, description, url, image, twitterCard, noindex }) {
  useEffect(() => {
    // Page title
    document.title = title;

    // Robots directive - defaults to indexable; pages opt out via noindex
    let robotsTag = document.querySelector('meta[name="robots"]');
    if (!robotsTag) {
      robotsTag = document.createElement("meta");
      robotsTag.name = "robots";
      document.head.appendChild(robotsTag);
    }
    robotsTag.content = noindex ? "noindex, nofollow" : "index, follow";

    // Standard meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = description;

    // Canonical URL — always points at the production origin/path, regardless
    // of which host actually served this build.
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = toCanonicalUrl(url);

    // Open Graph / Facebook
    const ogTags = [
      { prop: "og:title", content: title },
      { prop: "og:description", content: description },
      { prop: "og:url", content: url },
      { prop: "og:image", content: image },
    ];

    ogTags.forEach(({ prop, content }) => {
      let tag = document.querySelector(`meta[property="${prop}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", prop);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });

    // Twitter
    const twitterTags = [
      { name: "twitter:card", content: twitterCard || "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: image },
    ];

    twitterTags.forEach(({ name, content }) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, [title, description, url, image, twitterCard, noindex]);

  return null; // nothing renders in UI
}
